import React from "react";
import { useForm } from "react-hook-form";
import { Button, Label } from "@src/common/components";
import { useCustomLocation } from "../context";
import { LocationFormData } from "../types";

export const LocationForm: React.FC = () => {
  const { hasPinned, isLoading, submitLocation, onClose, submitButtonText } =
    useCustomLocation();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<LocationFormData>({
    defaultValues: {
      streetAddress: "",
      city: "",
    },
  });

  const watchStreetAddress = watch("streetAddress");
  const watchCity = watch("city");

  const isSubmitDisabled =
    !watchStreetAddress?.trim() || !watchCity?.trim() || !hasPinned || isLoading;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleSubmit(submitLocation)(e);
  };

  return (
    <form
      onSubmit={onSubmit}
      className="col-span-1 h-full overflow-y-auto px-2 flex flex-col"
    >
      <div className="flex flex-col h-full">
        <div className="space-y-4 flex-1 flex flex-col">
          <div className="flex flex-col">
            <Label htmlFor="streetAddress">
              Street Address{" "}
              <span className="text-orange-400 dark:text-orange-500">*</span>
            </Label>
            <input
              {...register("streetAddress", {
                required: "Street address is required",
              })}
              placeholder="Enter street address"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            {errors.streetAddress && (
              <span className="text-xs text-red-500 dark:text-red-400 mt-1 min-h-[20px]">
                {errors.streetAddress.message}
              </span>
            )}
          </div>

          <div className="flex flex-col">
            <Label htmlFor="city">
              City{" "}
              <span className="text-orange-400 dark:text-orange-500">*</span>
            </Label>
            <input
              {...register("city", { required: "City is required" })}
              placeholder="Enter city"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            {errors.city && (
              <span className="text-xs text-red-500 dark:text-red-400 mt-1 min-h-[20px]">
                {errors.city.message}
              </span>
            )}
          </div>

          <div
            className={`text-sm text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 p-2 rounded-md border border-amber-200 dark:border-amber-800 ${hasPinned ? "invisible" : ""}`}
          >
            Please drag the pin on the map to set the location coordinates
          </div>

          <div className="flex-1" />
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button onClick={onClose} color="secondary">
            Cancel
          </Button>
          <Button type="submit" isProcessing={isLoading} disabled={isSubmitDisabled}>
            {submitButtonText}
          </Button>
        </div>
      </div>
    </form>
  );
};
